import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import { router } from '@/routes/appRoutes';
import type { IncomingMessageOfType } from '@/store/listeners/poc/incomingMessageMatchers';
import { isIncomingMessageOfType } from '@/store/listeners/poc/incomingMessageMatchers';
import type { AppStartListening } from '@/store/listeners/types';
import type { RootState } from '@/store/rootReducer';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';

/**
 * Message-broker peer ids — the only peers allowed to drive a forced logout.
 * The peer id is the StrictSign publisher of the message and cannot be spoofed by
 * another mesh peer, so trusting only the broker keeps that power off every other
 * bootstrap node.
 */
const getTrustedBrokerPeerIds = (state: RootState): Set<string> => {
  const trusted = new Set<string>();
  for (const connection of state.connection.connections) {
    if (connection.messageBrokerPeerId !== null) {
      trusted.add(connection.messageBrokerPeerId);
    }
  }
  return trusted;
};

/**
 * Logs the user out when a `logout` P2P message arrives from the message broker
 * (e.g. session revocation / removal from the org). The origin is verified against
 * the broker's StrictSign peer id — a `logout` from any other peer is ignored so no
 * mesh participant (not even another bootstrap node) can force users offline.
 * Mirrors the manual logout in `useAppLayout`: `logoutThunk` (which tears down
 * libp2p/Helia and clears tokens) then redirect to `/login`.
 */
export function registerIncomingAuthListeners(startAppListening: AppStartListening): void {
  startAppListening({
    matcher: (action: UnknownAction): action is PayloadAction<IncomingMessageOfType<'logout'>> =>
      isIncomingMessageOfType(action, 'logout'),
    effect: (action, listenerApi) => {
      const { originPeerId } = action.payload.stats;
      const log = listenerApi.extra.loggerService.createLogger({ namespace: 'six3ro:auth:logout' });

      const trusted = getTrustedBrokerPeerIds(listenerApi.getState());
      if (originPeerId === null || !trusted.has(originPeerId)) {
        log.warn('Ignoring logout message — origin is not the trusted message broker', {
          originPeerId,
        });
        return;
      }

      log.info('Forced logout requested by message broker', { originPeerId });
      void listenerApi.dispatch(logoutThunk());
      void router.navigate({ to: '/login', replace: true });
    },
  });
}
